import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { PageHero, Section, fadeUp } from "../components/sections";
import SeoHead from "../components/SeoHead";
import { SERVICES, PROCESS } from "../lib/site";

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      <SeoHead
        title="Services"
        description="Custom software development, web & mobile applications, cloud & DevSecOps, AI & data engineering, quality engineering, product & UX design, dedicated engineering teams, and legacy modernisation — built for production from day one."
        path="/services"
      />
      <PageHero
        overline="Services"
        title={<>The disciplines we ship with, every engagement.</>}
        intro="From architectural diligence to dedicated engineering pods, every service we offer is calibrated for production from day one. No proof-of-concept theatre."
        testId="services-hero"
      />

      <Section className="bg-[#050505]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <p className="overline mb-6">How we engage</p>
            <h3
              className="font-serif-display text-3xl md:text-4xl text-white font-light leading-tight"
              style={{ letterSpacing: "-0.02em" }}
            >
              Choose the service. We bring the team, the methodology, the runway.
            </h3>
          </div>
          <div className="lg:col-span-8 space-y-px bg-[#27272A]">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.04 }}
                className="bg-[#050505] p-10 hover:bg-[#0A0A0C] transition-colors"
                data-testid={`service-row-${s.id}`}
              >
                <div className="flex items-start gap-8">
                  <p
                    className="font-serif-display italic text-3xl font-light hidden md:block"
                    style={{ color: "#C2533B" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <div className="flex-1">
                    <h3 className="font-serif-display text-2xl md:text-3xl text-white font-light mb-4">
                      {s.title}
                    </h3>
                    <p className="text-zinc-400 text-sm md:text-base leading-relaxed">{s.summary}</p>
                    <ul className="mt-6 space-y-2">
                      {s.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-3 text-zinc-500 text-sm">
                          <Check size={14} strokeWidth={1.5} style={{ color: "#C2533B" }} className="mt-1 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section testId="services-process" className="bg-[#0A0A0C] border-y border-white/5">
        <p className="overline mb-6">Our delivery method</p>
        <h2
          className="section-title text-4xl md:text-5xl text-white font-light text-balance max-w-4xl"
          style={{ letterSpacing: "-0.025em" }}
        >
          A four-stage cadence we have refined over a decade of enterprise delivery.
        </h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-px bg-[#27272A]">
          {PROCESS.map((p, i) => (
            <motion.div
              key={p.n}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="bg-[#0A0A0C] p-8"
            >
              <p className="font-serif-display italic text-4xl font-light" style={{ color: "#C2533B" }}>
                {p.n}
              </p>
              <h3 className="mt-4 font-serif-display text-xl text-white font-light">{p.title}</h3>
              <p className="mt-3 text-zinc-500 text-sm leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <h2
              className="font-serif-display font-light text-white leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(36px, 5vw, 64px)", letterSpacing: "-0.025em" }}
            >
              Tell us what your roadmap is missing.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact" className="btn-primary" data-testid="services-cta">
              Start a Project <ArrowUpRight size={14} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
